import { formatAssetClassLabel, isTradedAssetClassesField } from "./tradingViewBroker";

export { formatAssetClassLabel, isTradedAssetClassesField };

export const TRADOVATE_BROKER_SLUG = "tradovate";
export const NINJATRADER_BROKER_SLUG = "ninjatrader";

/** Brokers using Tradovate-style Orders + optional Cash History CSV import. */
export const FUTURES_ORDERS_CSV_BROKERS = [TRADOVATE_BROKER_SLUG, NINJATRADER_BROKER_SLUG] as const;

export type FuturesOrdersCsvBrokerSlug = (typeof FUTURES_ORDERS_CSV_BROKERS)[number];

/** Multipart field name for optional Cash History CSV (must be snake_case). */
export const FUTURES_ORDERS_CASH_HISTORY_FIELD = "cash_history_file";

const IMPORT_FIELD_LABELS: Record<string, string> = {
  time_zone: "Fill Time timezone",
};

const PLATFORM_DISPLAY_NAMES: Record<FuturesOrdersCsvBrokerSlug, string> = {
  tradovate: "Tradovate",
  ninjatrader: "NinjaTrader",
};

export function normalizeFuturesOrdersCsvBrokerSlug(
  brokerName: string | null | undefined
): FuturesOrdersCsvBrokerSlug | null {
  const slug = String(brokerName || "").toLowerCase();
  return FUTURES_ORDERS_CSV_BROKERS.includes(slug as FuturesOrdersCsvBrokerSlug)
    ? (slug as FuturesOrdersCsvBrokerSlug)
    : null;
}

export function isFuturesOrdersCsvBroker(brokerName: string | null | undefined): boolean {
  return normalizeFuturesOrdersCsvBrokerSlug(brokerName) != null;
}

export function isTradovateBroker(brokerName: string | null | undefined): boolean {
  return String(brokerName || "").toLowerCase() === TRADOVATE_BROKER_SLUG;
}

export function isNinjaTraderBroker(brokerName: string | null | undefined): boolean {
  return String(brokerName || "").toLowerCase() === NINJATRADER_BROKER_SLUG;
}

export function futuresOrdersPlatformDisplayName(
  brokerName: string | null | undefined
): string {
  const slug = normalizeFuturesOrdersCsvBrokerSlug(brokerName);
  return slug ? PLATFORM_DISPLAY_NAMES[slug] : "Broker";
}

/** Hidden on import/connect — account from CSV; asset class fixed on the server. */
export function isFuturesOrdersCsvHiddenImportField(field: string): boolean {
  const lower = field.toLowerCase();
  return (
    lower === "broker_name" ||
    lower === "account_name" ||
    lower === "account_code" ||
    lower === "account" ||
    isTradedAssetClassesField(field)
  );
}

export function isFuturesOrdersCsvHiddenCredentialField(field: string): boolean {
  return isFuturesOrdersCsvHiddenImportField(field);
}

export function futuresOrdersCsvImportFieldLabel(field: string, fallback: string): string {
  return IMPORT_FIELD_LABELS[field] ?? fallback;
}

export function buildFuturesOrdersCsvImportParams(
  brokerName: string,
  values: Record<string, string>
): Record<string, string | string[]> {
  const slug = normalizeFuturesOrdersCsvBrokerSlug(brokerName) ?? brokerName.toLowerCase();
  const timeZone = (values.time_zone || values.timeZone || "").trim();
  return {
    broker_name: slug,
    time_zone: timeZone,
  };
}

export function isFuturesOrdersCsvFile(file: File): boolean {
  return file.name.toLowerCase().endsWith(".csv");
}

export function futuresOrdersSupportsCashHistoryImport(optionalFields: string[]): boolean {
  return optionalFields.some(
    (f) => f.toLowerCase() === FUTURES_ORDERS_CASH_HISTORY_FIELD.toLowerCase()
  );
}

export function futuresOrdersCashHistoryFieldLabel(
  fieldLabels: Record<string, string> | undefined
): string {
  const fromApi = fieldLabels?.[FUTURES_ORDERS_CASH_HISTORY_FIELD]?.trim();
  return fromApi || "Cash History (optional)";
}

export function futuresOrdersCashHistoryFieldHint(
  fieldLabels: Record<string, string> | undefined,
  brokerName: string | null | undefined
): string {
  const label = fieldLabels?.[FUTURES_ORDERS_CASH_HISTORY_FIELD]?.trim();
  if (label && !label.toLowerCase().includes("optional")) {
    return label;
  }
  const platform = futuresOrdersPlatformDisplayName(brokerName);
  return `For trade fees and commissions. Export from ${platform} → Cash History. Use the same timezone as Orders. PnL still comes from Orders; this file only adds fees.`;
}

function readReportMetaFees(importResult: Record<string, unknown> | null | undefined): {
  feesMapped: number;
  feesTotalUsd: number;
} {
  const meta = (importResult?.report_meta ?? importResult?.reportMeta) as
    | Record<string, unknown>
    | undefined;
  const feesMapped = Number(meta?.fees_mapped ?? meta?.feesMapped ?? 0);
  const feesTotalUsd = Number(meta?.fees_total_usd ?? meta?.feesTotalUsd ?? 0);
  return {
    feesMapped: Number.isFinite(feesMapped) ? feesMapped : 0,
    feesTotalUsd: Number.isFinite(feesTotalUsd) ? feesTotalUsd : 0,
  };
}

export function formatFuturesOrdersFeesUsd(amount: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function getFuturesOrdersFeesFollowUpToasts(
  importResult: Record<string, unknown> | null | undefined,
  hadCashHistoryFile: boolean
): Array<{ title: string; description: string; status: "success" | "warning" | "info" }> {
  const { feesTotalUsd } = readReportMetaFees(importResult);
  if (hadCashHistoryFile) {
    if (feesTotalUsd > 0) {
      return [
        {
          title: "Fees imported",
          description: `${formatFuturesOrdersFeesUsd(feesTotalUsd)} in commissions and fees applied to matching fills.`,
          status: "success",
        },
      ];
    }
    return [
      {
        title: "No fees matched",
        description:
          "Cash History was uploaded but no fees were applied. Check that the timezone matches Orders and that the file covers the same account and dates.",
        status: "warning",
      },
    ];
  }
  return [];
}

export function futuresOrdersImportSuccessSuffix(
  importResult: Record<string, unknown> | null | undefined,
  hadCashHistoryFile: boolean
): string {
  if (hadCashHistoryFile) return "";
  const { feesTotalUsd } = readReportMetaFees(importResult);
  if (feesTotalUsd > 0) return "";
  return " Add Cash History CSV on a future import to include commissions and fees.";
}

export function formatFuturesOrdersFixedAssetClassesLabel(classes: string[]): string | null {
  const normalized = classes.map((c) => c.trim().toLowerCase()).filter(Boolean);
  if (normalized.length === 0) return null;
  if (normalized.length === 1 && normalized[0] === "futures") {
    return "Futures only";
  }
  return classes.map((c) => formatAssetClassLabel(c)).join(", ");
}
